import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import "@awesome.me/webawesome/dist/components/card/card.js";
import "@awesome.me/webawesome/dist/components/radio-group/radio-group.js";
import "@awesome.me/webawesome/dist/components/radio/radio.js";
import "@awesome.me/webawesome/dist/components/select/select.js";
import "@awesome.me/webawesome/dist/components/option/option.js";
import "@awesome.me/webawesome/dist/components/input/input.js";
import "@awesome.me/webawesome/dist/components/button/button.js";
import "@awesome.me/webawesome/dist/components/divider/divider.js";
import "@awesome.me/webawesome/dist/components/callout/callout.js";
import "@awesome.me/webawesome/dist/components/icon/icon.js";
import "@awesome.me/webawesome/dist/components/dialog/dialog.js";
import "@awesome.me/webawesome/dist/components/spinner/spinner.js";
import "../components/app-shell";
import { getDb, saveSettings } from "../db";
import { syncService, type SyncState } from "../services/sync";
import { settingsSyncService } from "../services/settings-sync";
import type {
  UserSettings,
  DoseIncrement,
  MealTimes,
  DataSharingLevel,
  TargetProfileType,
  CorrectionStackingMode,
} from "../types";
import {
  InsulinDelivery,
  QAInsulinType,
  BIInsulinType,
  BIFrequency,
  QA_INSULIN_NAMES,
  BI_INSULIN_NAMES,
  DOSE_INCREMENT_INFO,
  CORRECTION_STACKING_INFO,
  DEFAULT_SETTINGS,
  DEFAULT_MEAL_TIMES,
  DATA_SHARING_LABELS,
} from "../types";
import {
  TARGET_PROFILES,
  getUnitsPerCPFromSettings,
  getMissingSettings,
} from "../config/dafne";

@customElement("settings-page")
export class SettingsPage extends LitElement {
  @state() private settings: UserSettings = { ...DEFAULT_SETTINGS };
  @state() private saving = false;
  @state() private saved = false;
  @state() private syncMessage = "";
  @state() private showDeleteDialog = false;
  @state() private deleteConfirmText = "";
  @state() private deleting = false;
  @state() private deleteError = "";
  @state() private saveError = "";
  @state() private setupRequiredRedirect = false;
  @state() private setupRedirectFrom: "today" | "history" | "" = "";
  @state() private syncState: SyncState = syncService.state;
  @state() private isInitializing = true;

  private _syncUnsubscribe: (() => void) | null = null;
  private _settingsSyncUnsubscribe: (() => void) | null = null;
  private _boundSaveRequestHandler = this._handleSaveRequest.bind(this);

  static styles = css`
    :host {
      display: block;
    }

    .settings-content {
      padding: 1rem;
      max-width: 600px;
      margin: 0 auto;
    }

    h2 {
      font-size: 1.25rem;
      font-weight: 600;
      margin: 1.5rem 0 1rem;
      color: #212529;
    }

    h2:first-of-type {
      margin-top: 0;
    }

    .setting-row {
      margin-bottom: 1.25rem;
    }

    .setting-row label {
      display: block;
      font-weight: 500;
      margin-bottom: 0.5rem;
      color: #495057;
    }

    .setting-row .hint {
      font-size: 0.75rem;
      color: #6c757d;
      margin-top: 0.25rem;
    }

    .ratio-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 1rem;
    }

    .ratio-grid.meal-times {
      grid-template-columns: 1fr;
      max-width: 300px;
    }

    .ratio-grid.meal-times .ratio-item {
      flex-direction: row;
      align-items: center;
      gap: 1rem;
    }

    .ratio-grid.meal-times .ratio-item label {
      min-width: 80px;
      margin-bottom: 0;
    }

    .ratio-grid.meal-times .ratio-item wa-input {
      flex: 1;
    }

    .ratio-item {
      display: flex;
      flex-direction: column;
    }

    .ratio-item label {
      font-size: 0.75rem;
      font-weight: 500;
      color: #6c757d;
      margin-bottom: 0.25rem;
    }

    wa-card {
      margin-bottom: 1rem;
    }

    wa-card::part(body) {
      padding: 1rem;
    }

    .period-name {
      font-weight: 500;
      margin-bottom: 0.75rem;
      color: #212529;
    }

    wa-divider {
      --spacing: 1.5rem;
    }

    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      margin-top: 2rem;
      padding-bottom: 2rem;
    }

    .actions wa-button {
      flex: 1 1 auto;
      min-width: 140px;
    }

    @media (max-width: 480px) {
      .actions {
        flex-direction: column;
      }

      .actions wa-button {
        width: 100%;
      }
    }

    wa-callout {
      margin-bottom: 1rem;
    }

    .target-ranges-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.875rem;
      margin-top: 1rem;
    }

    .target-ranges-table th,
    .target-ranges-table td {
      padding: 0.5rem;
      text-align: left;
      border-bottom: 1px solid #dee2e6;
    }

    .target-ranges-table th {
      font-weight: 600;
      color: #495057;
      background: #f8f9fa;
    }

    .target-ranges-table td {
      color: #212529;
    }

    .target-ranges-table .range-value {
      font-family: monospace;
      color: #198754;
    }

    .profile-description {
      font-size: 0.875rem;
      color: #6c757d;
      margin-top: 0.5rem;
      font-style: italic;
    }

    .danger-zone {
      margin-top: 3rem;
      padding-top: 1.5rem;
      border-top: 2px solid #dc3545;
    }

    .danger-zone h2 {
      color: #dc3545;
    }

    .danger-zone-card {
      background: #fff5f5;
      border: 1px solid #dc3545;
      border-radius: 0.5rem;
      padding: 1rem;
    }

    .danger-zone-card h3 {
      margin: 0 0 0.5rem 0;
      font-size: 1rem;
      font-weight: 600;
      color: #dc3545;
    }

    .danger-zone-card p {
      margin: 0 0 1rem 0;
      font-size: 0.875rem;
      color: #495057;
    }

    .delete-confirm-input {
      margin-top: 1rem;
    }

    .delete-confirm-input label {
      display: block;
      font-size: 0.875rem;
      color: #495057;
      margin-bottom: 0.5rem;
    }

    .delete-confirm-input code {
      background: #f1f1f1;
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
      font-family: monospace;
      color: #dc3545;
    }

    /* Tablet+ layout */
    @media (min-width: 768px) {
      .settings-content {
        padding: 1.5rem;
      }

      .ratio-grid {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    window.addEventListener("save-request", this._boundSaveRequestHandler);
    const params = new URLSearchParams(window.location.search);
    this.setupRequiredRedirect = params.get("required") === "1";
    const from = params.get("from");
    this.setupRedirectFrom = from === "today" || from === "history" ? from : "";

    // Subscribe to sync state changes
    this._syncUnsubscribe = syncService.subscribe((state: SyncState) => {
      this.syncState = state;
    });

    // Subscribe to settings sync events (SSE updates)
    this._settingsSyncUnsubscribe = settingsSyncService.subscribe((event) => {
      if (event.dataChanged && event.data) {
        console.log("[SettingsPage] Settings updated via SSE");
        this.settings = event.data;
        this.saveError = "Settings were updated from another device.";
        setTimeout(() => {
          this.saveError = "";
        }, 5000);
      }
    });

    // Wait for initial sync before loading settings
    this.isInitializing = true;
    await this._waitForInitialSync();
    this.isInitializing = false;

    await this._loadSettings();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("save-request", this._boundSaveRequestHandler);
    if (this._syncUnsubscribe) {
      this._syncUnsubscribe();
      this._syncUnsubscribe = null;
    }
    if (this._settingsSyncUnsubscribe) {
      this._settingsSyncUnsubscribe();
      this._settingsSyncUnsubscribe = null;
    }
  }

  private _handleSaveRequest() {
    this._saveSettings();
  }

  /**
   * Wait for initial sync to complete (or timeout/go offline)
   */
  private async _waitForInitialSync(): Promise<void> {
    const currentState = syncService.state;
    if (currentState.initialSyncComplete || currentState.isConfirmedFreshUser) {
      return;
    }

    if (!navigator.onLine) {
      return;
    }

    if (currentState.status === "idle") {
      syncService.checkAndSync();
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        console.warn(
          "[SettingsPage] Initial sync timeout - proceeding without sync",
        );
        resolve();
      }, 15000);

      const checkState = (state: SyncState) => {
        if (
          state.initialSyncComplete ||
          state.isConfirmedFreshUser ||
          state.status === "error" ||
          state.status === "offline"
        ) {
          clearTimeout(timeout);
          resolve();
        }
      };

      // Check current state immediately
      checkState(syncService.state);

      // Also listen for changes
      const existingUnsub = this._syncUnsubscribe;
      this._syncUnsubscribe = syncService.subscribe((state: SyncState) => {
        this.syncState = state;
        checkState(state);
      });
      // Keep old subscription behavior by calling it
      if (existingUnsub) existingUnsub();
    });
  }

  private async _loadSettings() {
    try {
      // Try to sync from server first when online (server always wins)
      if (navigator.onLine) {
        const syncResult = await settingsSyncService.checkAndSync();
        if (syncResult.updated) {
          console.log(
            `[Settings] Synced from server, dataChanged: ${syncResult.dataChanged}`,
          );
        }
      }

      // Load from local DB (which now has server data if sync succeeded)
      const stored = await getDb().settings.get("settings");
      if (stored) {
        this.settings = stored;
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
    }
  }

  private async _saveSettings() {
    this.saving = true;
    this.saveError = "";
    try {
      // Use saveSettings from operations.ts - handles server sync with optimistic concurrency
      const result = await saveSettings(this.settings);

      if (result.conflict) {
        // Server had different data - server won, reload UI
        if (result.serverData) {
          this.settings = result.serverData;
        }
        this.saveError =
          "Settings were updated from another device. Please review and try again.";
        setTimeout(() => {
          this.saveError = "";
        }, 8000);
      } else if (result.offline) {
        // Saved offline - update component state with saved data
        if (result.savedData) {
          this.settings = result.savedData;
        }
        this.saved = true;
        this.syncMessage = "Saved offline - will sync when online";
        setTimeout(() => {
          this.saved = false;
          this.syncMessage = "";
        }, 3000);
      } else {
        // Normal success - CRITICAL: update component state with new version
        if (result.savedData) {
          this.settings = result.savedData;
        }
        this.saved = true;
        setTimeout(() => {
          this.saved = false;
        }, 3000);
      }
    } catch (error) {
      console.error("Failed to save settings:", error);
      this.saveError =
        error instanceof Error ? error.message : "Failed to save";
      setTimeout(() => {
        this.saveError = "";
      }, 10000);
    } finally {
      this.saving = false;
    }
  }

  private _updateSetting<K extends keyof UserSettings>(
    key: K,
    value: UserSettings[K],
  ) {
    this.settings = { ...this.settings, [key]: value };
  }

  private _updateCPRatio(period: string, unitsPerCP: number) {
    this.settings = {
      ...this.settings,
      cpRatios: { ...this.settings.cpRatios, [period]: unitsPerCP },
    };
  }

  private _updateMealTime(key: keyof MealTimes, value: string) {
    const currentMealTimes = this.settings.mealTimes || {
      ...DEFAULT_MEAL_TIMES,
    };
    this.settings = {
      ...this.settings,
      mealTimes: { ...currentMealTimes, [key]: value },
    };
  }

  private _updateDataSharing(
    field: "allowDafneDataSharing" | "allowCgmDataSharing",
    value: DataSharingLevel,
  ) {
    const currentDataSharing = this.settings.dataSharing || {
      allowDafneDataSharing: "none" as DataSharingLevel,
      allowCgmDataSharing: "none" as DataSharingLevel,
    };
    this.settings = {
      ...this.settings,
      dataSharing: {
        ...currentDataSharing,
        [field]: value,
        consentUpdatedAt: new Date(),
      },
    };
  }

  // Use centralized conversion from config/dafne/insulin.ts
  private _getUnitsPerCP(period: string): number {
    return getUnitsPerCPFromSettings(this.settings.cpRatios, period);
  }

  private _openDeleteDialog() {
    this.deleteConfirmText = "";
    this.deleteError = "";
    this.showDeleteDialog = true;
  }

  private _closeDeleteDialog() {
    this.showDeleteDialog = false;
    this.deleteConfirmText = "";
    this.deleteError = "";
  }

  private async _confirmDeleteAllData() {
    const CONFIRM_PHRASE = "DELETE ALL MY DATA";

    if (this.deleteConfirmText !== CONFIRM_PHRASE) {
      this.deleteError = `Please type "${CONFIRM_PHRASE}" exactly to confirm`;
      return;
    }

    this.deleting = true;
    this.deleteError = "";

    try {
      const result = await syncService.deleteAllData();

      if (result.success) {
        this._closeDeleteDialog();
        // Redirect to login after data deletion
        window.location.href = "/login";
      } else {
        this.deleteError = result.error || "Failed to delete data";
      }
    } catch (error) {
      this.deleteError =
        error instanceof Error ? error.message : "Unknown error";
    } finally {
      this.deleting = false;
    }
  }

  private _renderTargetRangesTable() {
    const profile = TARGET_PROFILES[this.settings.targetProfile || "standard"];
    return html`
      <table class="target-ranges-table">
        <thead>
          <tr>
            <th>Time Period</th>
            <th>Target Range (mmol/L)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Before Breakfast (fasting)</td>
            <td class="range-value">
              ${profile.preMeal.B.low} - ${profile.preMeal.B.high}
            </td>
          </tr>
          <tr>
            <td>Before Lunch</td>
            <td class="range-value">
              ${profile.preMeal.L.low} - ${profile.preMeal.L.high}
            </td>
          </tr>
          <tr>
            <td>Before Evening Meal</td>
            <td class="range-value">
              ${profile.preMeal.EM.low} - ${profile.preMeal.EM.high}
            </td>
          </tr>
          <tr>
            <td>Before Bed</td>
            <td class="range-value">
              ${profile.preMeal.BT.low} - ${profile.preMeal.BT.high}
            </td>
          </tr>
          <tr>
            <td>3AM Check (overnight)</td>
            <td class="range-value">
              ${profile.preMeal["3AM"].low} - ${profile.preMeal["3AM"].high}
            </td>
          </tr>
          <tr>
            <td>2 Hours After Meals</td>
            <td class="range-value">
              ${profile.postMeal.low} - ${profile.postMeal.high}
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  render() {
    // Show loading state while initializing (waiting for sync)
    if (this.isInitializing) {
      return html`
        <app-shell
          pageTitle="Settings"
          .showSave=${true}
          .saveDisabled=${true}
          .saveLoading=${false}
        >
          <div
            style="display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 50vh; gap: 1rem; color: #6c757d;"
          >
            <wa-spinner style="font-size: 2rem;"></wa-spinner>
            <p>Loading your settings...</p>
          </div>
        </app-shell>
      `;
    }

    const missingRequired = getMissingSettings(this.settings);
    const showSetupWarning =
      this.setupRequiredRedirect && missingRequired.length > 0;
    const sourceLabel =
      this.setupRedirectFrom === "history"
        ? "History"
        : this.setupRedirectFrom === "today"
          ? "Today"
          : "the app";

    // Show warning if sync hasn't completed
    const syncNotComplete =
      !this.syncState.initialSyncComplete &&
      !this.syncState.isConfirmedFreshUser &&
      navigator.onLine;

    return html`
      <app-shell
        pageTitle="Settings"
        .showSave=${true}
        .saveDisabled=${this.saving}
        .saveLoading=${this.saving}
      >
        <div class="settings-content">
          ${syncNotComplete
            ? html`
                <wa-callout variant="warning" open style="margin-bottom: 1rem;">
                  <wa-icon slot="icon" name="triangle-exclamation"></wa-icon>
                  <strong>Sync in progress</strong>
                  <div style="margin-top: 0.25rem;">
                    Waiting for your data to sync from the server. If you have
                    existing settings on another device, please wait a moment.
                  </div>
                </wa-callout>
              `
            : ""}
          ${showSetupWarning
            ? html`
                <wa-callout variant="danger" open>
                  <wa-icon slot="icon" name="triangle-exclamation"></wa-icon>
                  <strong>Setup required before using ${sourceLabel}</strong>
                  <div style="margin-top: 0.5rem;">
                    Complete these required DAFNE settings first:
                    <ul style="margin: 0.5rem 0 0 1rem; padding: 0;">
                      ${missingRequired.map((item) => html`<li>${item}</li>`)}
                    </ul>
                  </div>
                </wa-callout>
              `
            : ""}
          <h2>Personal</h2>

          <div class="setting-row">
            <label>Preferred name</label>
            <wa-input
              value=${this.settings.nickname || ""}
              placeholder="e.g. Kyley"
              @input=${(e: Event) =>
                this._updateSetting(
                  "nickname",
                  (e.target as HTMLInputElement).value,
                )}
            ></wa-input>
            <div class="hint">
              Shown on the Dashboard greeting. Leave blank to use your account
              name.
            </div>
          </div>

          <wa-divider></wa-divider>

          <h2>Insulin Delivery</h2>

          <div class="setting-row">
            <label>Delivery Method</label>
            <wa-radio-group
              value=${this.settings.insulinDelivery || "mdi"}
              @change=${(e: Event) =>
                this._updateSetting(
                  "insulinDelivery",
                  (e.target as HTMLInputElement).value as InsulinDelivery,
                )}
            >
              <wa-radio value="mdi">MDI (Injections)</wa-radio>
              <wa-radio value="pump">Insulin Pump</wa-radio>
            </wa-radio-group>
          </div>

          <div class="setting-row">
            <label>Minimum Dose Increment</label>
            <wa-select
              value=${String(this.settings.doseIncrement || 0.5)}
              @change=${(e: Event) =>
                this._updateSetting(
                  "doseIncrement",
                  Number(
                    (e.target as HTMLSelectElement).value,
                  ) as DoseIncrement,
                )}
            >
              ${(
                Object.keys(DOSE_INCREMENT_INFO) as unknown as DoseIncrement[]
              ).map(
                (value) => html`
                  <wa-option value=${String(value)}>
                    ${DOSE_INCREMENT_INFO[value].label} -
                    ${DOSE_INCREMENT_INFO[value].description}
                  </wa-option>
                `,
              )}
            </wa-select>
            <div class="hint">
              The smallest dose your pen or pump can deliver. Recommendations
              will be rounded to this increment.
            </div>
          </div>

          <wa-divider></wa-divider>

          <h2>Quick-Acting Insulin (QA)</h2>

          <div class="setting-row">
            <label>Quick-Acting Insulin Type</label>
            <wa-select
              value=${this.settings.qaInsulinType}
              @change=${(e: Event) =>
                this._updateSetting(
                  "qaInsulinType",
                  (e.target as HTMLSelectElement).value as QAInsulinType,
                )}
            >
              ${Object.entries(QA_INSULIN_NAMES).map(
                ([value, label]) => html`
                  <wa-option value=${value}>${label}</wa-option>
                `,
              )}
            </wa-select>
            <div class="hint">Taken with meals to cover carbs eaten</div>
          </div>

          <wa-divider></wa-divider>

          <h2>Long-Acting Insulin</h2>

          <div class="setting-row">
            <label>Long-Acting Insulin Type</label>
            <wa-select
              value=${this.settings.biInsulinType}
              @change=${(e: Event) =>
                this._updateSetting(
                  "biInsulinType",
                  (e.target as HTMLSelectElement).value as BIInsulinType,
                )}
            >
              ${Object.entries(BI_INSULIN_NAMES).map(
                ([value, label]) => html`
                  <wa-option value=${value}>${label}</wa-option>
                `,
              )}
            </wa-select>
          </div>

          <div class="setting-row">
            <label>Long-Acting Frequency</label>
            <wa-radio-group
              value=${this.settings.biFrequency}
              @change=${(e: Event) =>
                this._updateSetting(
                  "biFrequency",
                  (e.target as HTMLInputElement).value as BIFrequency,
                )}
            >
              <wa-radio value="once">Once Daily</wa-radio>
              <wa-radio value="twice">Twice Daily</wa-radio>
            </wa-radio-group>
            <div class="hint">
              NPH insulins (Protaphane, Humulin NPH) typically require twice
              daily dosing
            </div>
          </div>

          <wa-divider></wa-divider>

          <h2>CP Ratios</h2>
          <p class="hint" style="margin-bottom: 1rem;">
            Units per CP (10g carbs). Example: 1 means 1 unit for every 10g
            carbs. 3 means 3 units for every 10g carbs (tighter control).
          </p>

          <div class="ratio-grid">
            <div class="ratio-item">
              <label>Breakfast</label>
              <wa-input
                type="number"
                min="0.5"
                max="10"
                step="0.5"
                .value=${String(this._getUnitsPerCP("B"))}
                @input=${(e: Event) =>
                  this._updateCPRatio(
                    "B",
                    Number((e.target as HTMLInputElement).value),
                  )}
              ></wa-input>
            </div>
            <div class="ratio-item">
              <label>Lunch</label>
              <wa-input
                type="number"
                min="0.5"
                max="10"
                step="0.5"
                .value=${String(this._getUnitsPerCP("L"))}
                @input=${(e: Event) =>
                  this._updateCPRatio(
                    "L",
                    Number((e.target as HTMLInputElement).value),
                  )}
              ></wa-input>
            </div>
            <div class="ratio-item">
              <label>Evening Meal</label>
              <wa-input
                type="number"
                min="0.5"
                max="10"
                step="0.5"
                .value=${String(this._getUnitsPerCP("EM"))}
                @input=${(e: Event) =>
                  this._updateCPRatio(
                    "EM",
                    Number((e.target as HTMLInputElement).value),
                  )}
              ></wa-input>
            </div>
          </div>

          <wa-divider></wa-divider>

          <h2>Correction Factor</h2>

          <div class="setting-row">
            <label>ISF (Insulin Sensitivity Factor)</label>
            <wa-input
              type="number"
              min="0.5"
              max="10"
              step="0.1"
              .value=${String(this.settings.correctionFactor || "")}
              placeholder="e.g., 2.0"
              @input=${(e: Event) =>
                this._updateSetting(
                  "correctionFactor",
                  Number((e.target as HTMLInputElement).value),
                )}
            ></wa-input>
            <div class="hint">
              How much 1 unit of QA lowers your BG (in mmol/L). Example: 2.0
              means 1u drops BG by 2.0 mmol/L.
            </div>
          </div>

          <div class="setting-row">
            <label>IOB / Stacking Behaviour</label>
            <wa-radio-group
              value=${this.settings.correctionStacking || "no-stacking"}
              @change=${(e: Event) =>
                this._updateSetting(
                  "correctionStacking",
                  (e.target as HTMLInputElement)
                    .value as CorrectionStackingMode,
                )}
            >
              ${Object.entries(CORRECTION_STACKING_INFO).map(
                ([value, info]) => html`
                  <wa-radio value=${value}>${info.label}</wa-radio>
                `,
              )}
            </wa-radio-group>
            <div class="hint">
              ${CORRECTION_STACKING_INFO[
                this.settings.correctionStacking || "no-stacking"
              ].description}
            </div>
          </div>

          <wa-divider></wa-divider>

          <h2>Target Profile</h2>

          <div class="setting-row">
            <label>Which target range profile should you use?</label>
            <wa-radio-group
              value=${this.settings.targetProfile || "standard"}
              @change=${(e: Event) =>
                this._updateSetting(
                  "targetProfile",
                  (e.target as HTMLInputElement).value as TargetProfileType,
                )}
            >
              <wa-radio value="standard">Standard</wa-radio>
              <wa-radio value="upper">Upper</wa-radio>
            </wa-radio-group>
            <div class="profile-description">
              ${TARGET_PROFILES[this.settings.targetProfile || "standard"]
                .description}
            </div>
          </div>

          ${this._renderTargetRangesTable()}

          <wa-divider></wa-divider>

          <h2>Typical Meal Times</h2>

          <div class="setting-row">
            <div class="hint" style="margin-bottom: 0.75rem;">
              Your typical schedule helps suggest the right period when adding
              entries.
            </div>
            <div class="ratio-grid meal-times">
              <div class="ratio-item">
                <label>Wake Up</label>
                <wa-input
                  type="time"
                  .value=${this.settings.mealTimes?.wakeUp ||
                  DEFAULT_MEAL_TIMES.wakeUp}
                  @input=${(e: Event) =>
                    this._updateMealTime(
                      "wakeUp",
                      (e.target as HTMLInputElement).value,
                    )}
                ></wa-input>
              </div>
              <div class="ratio-item">
                <label>Breakfast</label>
                <wa-input
                  type="time"
                  .value=${this.settings.mealTimes?.breakfast ||
                  DEFAULT_MEAL_TIMES.breakfast}
                  @input=${(e: Event) =>
                    this._updateMealTime(
                      "breakfast",
                      (e.target as HTMLInputElement).value,
                    )}
                ></wa-input>
              </div>
              <div class="ratio-item">
                <label>Lunch</label>
                <wa-input
                  type="time"
                  .value=${this.settings.mealTimes?.lunch ||
                  DEFAULT_MEAL_TIMES.lunch}
                  @input=${(e: Event) =>
                    this._updateMealTime(
                      "lunch",
                      (e.target as HTMLInputElement).value,
                    )}
                ></wa-input>
              </div>
              <div class="ratio-item">
                <label>Dinner</label>
                <wa-input
                  type="time"
                  .value=${this.settings.mealTimes?.dinner ||
                  DEFAULT_MEAL_TIMES.dinner}
                  @input=${(e: Event) =>
                    this._updateMealTime(
                      "dinner",
                      (e.target as HTMLInputElement).value,
                    )}
                ></wa-input>
              </div>
              <div class="ratio-item">
                <label>Bedtime</label>
                <wa-input
                  type="time"
                  .value=${this.settings.mealTimes?.bedtime ||
                  DEFAULT_MEAL_TIMES.bedtime}
                  @input=${(e: Event) =>
                    this._updateMealTime(
                      "bedtime",
                      (e.target as HTMLInputElement).value,
                    )}
                ></wa-input>
              </div>
            </div>
          </div>

          <wa-divider></wa-divider>

          <h2>Data Sharing</h2>
          <p class="hint" style="margin-bottom: 1rem;">
            Choose how your data may be used by your clinic for training and
            support purposes.
          </p>

          <div class="setting-row">
            <label>DAFNE Log Data</label>
            <wa-radio-group
              value=${this.settings.dataSharing?.allowDafneDataSharing ||
              "none"}
              @change=${(e: Event) =>
                this._updateDataSharing(
                  "allowDafneDataSharing",
                  (e.target as HTMLInputElement).value as DataSharingLevel,
                )}
            >
              ${Object.entries(DATA_SHARING_LABELS).map(
                ([value, info]) => html`
                  <wa-radio value=${value}>${info.label}</wa-radio>
                `,
              )}
            </wa-radio-group>
            <div class="hint">
              ${DATA_SHARING_LABELS[
                this.settings.dataSharing?.allowDafneDataSharing || "none"
              ].description}
            </div>
          </div>

          <div class="setting-row">
            <label>CGM Data</label>
            <wa-radio-group
              value=${this.settings.dataSharing?.allowCgmDataSharing || "none"}
              @change=${(e: Event) =>
                this._updateDataSharing(
                  "allowCgmDataSharing",
                  (e.target as HTMLInputElement).value as DataSharingLevel,
                )}
            >
              ${Object.entries(DATA_SHARING_LABELS).map(
                ([value, info]) => html`
                  <wa-radio value=${value}>${info.label}</wa-radio>
                `,
              )}
            </wa-radio-group>
            <div class="hint">
              ${DATA_SHARING_LABELS[
                this.settings.dataSharing?.allowCgmDataSharing || "none"
              ].description}
            </div>
          </div>

          ${this.saved
            ? html`
                <wa-callout variant="success" open style="margin-bottom: 1rem;">
                  <wa-icon slot="icon" name="circle-check"></wa-icon>
                  Settings saved successfully
                </wa-callout>
              `
            : ""}
          ${this.saveError
            ? html`
                <wa-callout variant="danger" open style="margin-bottom: 1rem;">
                  <wa-icon slot="icon" name="circle-exclamation"></wa-icon>
                  ${this.saveError}
                </wa-callout>
              `
            : ""}

          <div class="actions">
            <wa-button
              variant="brand"
              size="large"
              ?loading=${this.saving}
              @click=${this._saveSettings}
            >
              Save Settings
            </wa-button>
          </div>

          ${this.syncMessage
            ? html`
                <wa-callout variant="brand" open style="margin-top: 1rem;">
                  <wa-icon slot="icon" name="circle-info"></wa-icon>
                  ${this.syncMessage}
                </wa-callout>
              `
            : ""}

          <div class="danger-zone">
            <h2>Danger Zone</h2>
            <div class="danger-zone-card">
              <h3>Delete All Data</h3>
              <p>
                Permanently delete all your data from this device and the
                server. This action cannot be undone. Your account will remain
                but all log entries, settings, and CGM data will be erased.
              </p>
              <wa-button
                variant="danger"
                outline
                @click=${this._openDeleteDialog}
              >
                <wa-icon slot="prefix" name="trash"></wa-icon>
                Delete All My Data
              </wa-button>
            </div>
          </div>

          <wa-dialog
            label="Delete All Data"
            ?open=${this.showDeleteDialog}
            @wa-request-close=${this._closeDeleteDialog}
          >
            <wa-callout variant="danger" open>
              <wa-icon slot="icon" name="triangle-exclamation"></wa-icon>
              <strong>Warning:</strong> This will permanently delete all your
              data. This cannot be undone.
            </wa-callout>

            <p style="margin-top: 1rem;">
              All your log entries, CGM readings, settings, and synchronised
              data will be permanently deleted from both this device and the
              server.
            </p>

            <div class="delete-confirm-input">
              <label>
                To confirm, type <code>DELETE ALL MY DATA</code> below:
              </label>
              <wa-input
                placeholder="Type confirmation phrase"
                .value=${this.deleteConfirmText}
                @input=${(e: Event) =>
                  (this.deleteConfirmText = (
                    e.target as HTMLInputElement
                  ).value)}
              ></wa-input>
            </div>

            ${this.deleteError
              ? html`
                  <wa-callout variant="danger" open style="margin-top: 1rem;">
                    ${this.deleteError}
                  </wa-callout>
                `
              : ""}

            <div
              slot="footer"
              style="display: flex; gap: 0.5rem; justify-content: flex-end;"
            >
              <wa-button @click=${this._closeDeleteDialog}>Cancel</wa-button>
              <wa-button
                variant="danger"
                ?loading=${this.deleting}
                @click=${this._confirmDeleteAllData}
              >
                Delete All Data
              </wa-button>
            </div>
          </wa-dialog>
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "settings-page": SettingsPage;
  }
}
